import React, { useEffect, useMemo } from "react";
import { getTheme } from "@/lib/theme";

const rand = (min, max) => min + Math.random() * (max - min);

// Full-screen themed transition played when a business is chosen.
// kind: "fire" (Baking Jay's — gold & blue flames) | "rocks" (Ruby's — falling rocks)
export function LaunchTransition({ businessId, onDone, duration = 2000 }) {
  const theme = getTheme(businessId);
  const kind = businessId === "rubys" ? "rocks" : "fire";

  useEffect(() => {
    const t = setTimeout(onDone, duration);
    return () => clearTimeout(t);
  }, [onDone, duration]);

  const embers = useMemo(
    () =>
      Array.from({ length: 46 }).map(() => ({
        left: rand(0, 100),
        size: rand(6, 18),
        delay: rand(0, 0.9),
        dur: rand(1.1, 2),
        blue: Math.random() < 0.4,
        drift: rand(-40, 40),
      })),
    []
  );

  const flames = useMemo(
    () =>
      Array.from({ length: 14 }).map((_, i) => ({
        left: (i / 14) * 100 + rand(-3, 3),
        w: rand(60, 120),
        h: rand(140, 300),
        delay: rand(0, 0.4),
        dur: rand(0.5, 0.9),
        blue: i % 3 === 0,
      })),
    []
  );

  const rocks = useMemo(
    () =>
      Array.from({ length: 28 }).map(() => ({
        left: rand(0, 100),
        size: rand(18, 60),
        delay: rand(0, 0.7),
        dur: rand(0.9, 1.6),
        spin: rand(180, 720) * (Math.random() < 0.5 ? -1 : 1),
        shade: rand(0.25, 0.6),
        radius: `${rand(30, 60)}% ${rand(40, 70)}% ${rand(35, 65)}% ${rand(40, 70)}%`,
        ruby: Math.random() < 0.25,
      })),
    []
  );

  const dust = useMemo(
    () => Array.from({ length: 8 }).map((_, i) => ({ left: (i / 8) * 100 + rand(-4, 4), delay: rand(0.5, 1.3), size: rand(80, 160) })),
    []
  );

  return (
    <div
      data-testid="launch-transition"
      className="fixed inset-0 z-[100] overflow-hidden"
      style={{
        background: kind === "fire" ? "radial-gradient(circle at 50% 90%, #16204a 0%, #060810 70%)" : "radial-gradient(circle at 50% 10%, #3a0a12 0%, #08060a 70%)",
        animation: `overlayIn 0.35s ease both${kind === "rocks" ? ", screenShake 0.5s ease-in-out 0.9s 2" : ""}`,
      }}
    >
      {kind === "fire" ? (
        <>
          {/* Flames anchored to bottom */}
          <div className="absolute inset-x-0 bottom-0 h-1/2">
            {flames.map((f, i) => (
              <div
                key={i}
                style={{
                  position: "absolute",
                  bottom: -20,
                  left: `${f.left}%`,
                  width: f.w,
                  height: f.h,
                  transformOrigin: "bottom center",
                  borderRadius: "50% 50% 45% 45% / 70% 70% 30% 30%",
                  background: f.blue
                    ? "radial-gradient(ellipse at 50% 100%, #bfe0ff 0%, #4f8cff 35%, #1b3ea8 70%, transparent 100%)"
                    : "radial-gradient(ellipse at 50% 100%, #fff2c4 0%, #f6c34a 30%, #d98a1f 65%, transparent 100%)",
                  filter: "blur(3px)",
                  mixBlendMode: "screen",
                  animation: `flameFlicker ${f.dur}s ease-in-out ${f.delay}s infinite`,
                }}
              />
            ))}
          </div>
          {/* Rising embers */}
          {embers.map((e, i) => (
            <span
              key={i}
              style={{
                position: "absolute",
                bottom: 0,
                left: `${e.left}%`,
                width: e.size,
                height: e.size,
                borderRadius: "50%",
                background: e.blue ? "#7fb4ff" : "#f7cd5e",
                boxShadow: e.blue ? "0 0 12px 3px #4f8cff" : "0 0 12px 3px #f6b73c",
                marginLeft: e.drift,
                animation: `emberRise ${e.dur}s ease-in ${e.delay}s both`,
              }}
            />
          ))}
        </>
      ) : (
        <>
          {rocks.map((r, i) => (
            <span
              key={i}
              style={{
                position: "absolute",
                top: 0,
                left: `${r.left}%`,
                width: r.size,
                height: r.size * rand(0.7, 1),
                borderRadius: r.radius,
                "--spin": `${r.spin}deg`,
                background: r.ruby
                  ? `linear-gradient(135deg, #b3324a, #6d0f1e)`
                  : `linear-gradient(135deg, rgba(120,110,100,${r.shade + 0.2}), rgba(50,44,40,${r.shade}))`,
                boxShadow: "inset -3px -4px 8px rgba(0,0,0,0.5), inset 2px 2px 5px rgba(255,255,255,0.12)",
                animation: `rockFall ${r.dur}s cubic-bezier(0.45,0,0.9,0.5) ${r.delay}s both`,
              }}
            />
          ))}
          {/* Impact dust near bottom */}
          <div className="absolute inset-x-0 bottom-0 h-24">
            {dust.map((d, i) => (
              <span
                key={i}
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: `${d.left}%`,
                  width: d.size,
                  height: d.size,
                  borderRadius: "50%",
                  background: "radial-gradient(circle, rgba(180,120,110,0.5) 0%, transparent 70%)",
                  animation: `dustPuff 1.1s ease-out ${d.delay}s both`,
                }}
              />
            ))}
          </div>
        </>
      )}

      {/* Brand mark reveal */}
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none" style={{ animation: "brandReveal 2s ease-in-out both" }}>
        <div
          className="h-24 w-24 rounded-full flex items-center justify-center font-display text-5xl font-bold"
          style={{
            background: theme.vars["--accent"],
            color: "#1a1400",
            boxShadow: kind === "fire" ? "0 0 60px 20px rgba(246,183,60,0.6)" : "0 0 60px 20px rgba(179,50,74,0.55)",
          }}
        >
          {theme.name.charAt(0)}
        </div>
        <div className="font-display text-3xl mt-5 text-white drop-shadow-lg">{theme.name}</div>
      </div>
    </div>
  );
}
